import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin, switchMap, map, catchError, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { handleError } from './handle-error';
import { RAW_HTTP_ERROR } from '../interceptors/auth.interceptor';
import { ProductManagementService, SubscriptionProduct } from './product-management.service';
import { SubscriptionItem as PmSubscriptionItem } from '../model/product-management.model';
import {
  InvoiceType,
  InvoiceStatus,
  SubscriptionStatus,
  PaymentMethod,
  BillingCycle,
  CreditBalance,
  CreditTransaction,
  SubscriptionItem,
  Subscription,
  Payment,
  InvoiceLineItem,
  Invoice,
  PaymentInstrument,
  PaymentProfile,
  InstrumentType,
  InstrumentStatus,
  UpdatePaymentProfileRequest,
  CreatePaymentInstrumentRequest,
  BillingStatistics,
  BillingAccount,
  AvailableProduct,
  SubscriptionHistoryEntry,
  SubscriptionEndReason,
  SubscriptionItemLocation,
  BusinessLocation,
  InvoiceCollection,
  CollectionBlockedBy,
  InvoiceListFilters,
  InvoiceSummaryFilters,
  InvoicePage,
  InvoiceSummary
} from '../model/subscription-billing';

// Re-export all types for consumers
export * from '../model/subscription-billing';

// Feature code (lower-case) → Material icon. Used when building
// SubscriptionProductRef objects from backend responses that don't carry icons.
const FEATURE_ICONS: Record<string, string> = {
  'pos_terminal': 'point_of_sale',
  'pos_system': 'point_of_sale',
  'online_booking': 'calendar_month',
  'multi_location': 'store',
  'inventory_management': 'inventory',
  'inventory': 'inventory',
  'staff_mgmt': 'groups',
  'marketing': 'campaign',
  'sms_send': 'sms',
  'api_calls': 'api'
};

// =============================================================================
// Backend response shapes (odin moneta DTOs)
// =============================================================================

// Odin's PayInvoiceResponse (`PaymentDto.kt`) — the synchronous gateway verdict for :pay.
export interface PayInvoiceResult {
  invoiceId: string;
  invoiceStatus: string;
  amountPaid: number; // cents
  amountDue: number;  // cents
  gatewaySuccess: boolean | null; // null when fully paid by credits (no card attempt)
  errorCode: string | null;
  errorMessage: string | null;
}

// Subset of odin's BillingResultResponse (`BillingDto.kt`) — what the demo trigger UI reads.
export interface TriggerBillingResult {
  invoicesCreated: number;
  totalAmount: number; // cents
  currency: string;
  paymentIntentStatus: string | null;
}

interface InvoiceSummaryResponse {
  id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  total: number; // cents
  periodStart: string;
  periodEnd: string;
  lineItemCount: number;
}

interface InvoiceLineItemResponse {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  locationName?: string;
  periodStart?: string;
  periodEnd?: string;
  isProrated: boolean;
}

interface InvoiceCollectionResponse {
  status: string;
  totalAttempts: number;
  maxAttempts: number;
  nextRetryAt?: string;
  lastFailureReason?: string;
  lastFailureMessage?: string;
  blockedBy?: CollectionBlockedBy;
}

interface InvoiceDetailResponse {
  id: string;
  invoiceNumber: string;
  type: string;
  status: string;
  currency: string;
  periodStart: string;
  periodEnd: string;
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  total: number;
  amountPaid: number;
  amountDue: number;
  dueDate?: string;
  paidAt?: string;
  createdAt: string;
  lineItems: InvoiceLineItemResponse[];
  referenceInvoiceNumber?: string;
  collection?: InvoiceCollectionResponse;
}

/**
 * odin wraps every list in this envelope (`common/model/Model.kt`). Getting the field
 * names wrong is silent: the list parses as empty rather than failing, which is what made
 * the invoices table look permanently unwired.
 */
interface PaginatedResult<T> {
  items: T[];
  total?: number | null;
  nextKey?: string | null;
}

interface PaymentResponse {
  id: string;
  invoiceId: string;
  invoiceNumber: string;
  amount: number;
  cashBasis: number;
  currency: string;
  method: string;
  referenceNumber?: string;
  notes?: string;
  recordedBy?: string;
  reversalOfPaymentId?: string;
  processedAt?: string;
  createdAt: string;
}

interface PaymentInstrumentResponse {
  id: string;
  type: string;
  status: string;
  gatewayName: string;
  displayName: string;
  lastFour?: string;
  brand?: string;
  expiresAt?: string;
  bankName?: string;
  isDefault: boolean;
  createdAt: string;
}

interface PaymentProfileResponse {
  id: string;
  businessId: string;
  gatewayCustomerId?: string;
  billingEmail?: string;
  autoPayEnabled: boolean;
  defaultInstrumentId?: string;
  instruments: PaymentInstrumentResponse[];
  createdAt: string;
}

interface CreditBalanceResponse {
  id: string;
  businessId: string;
  balanceAmount: number; // cents
  currency: string;
  updatedAt: string;
}

interface CreditBalanceTransactionResponse {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  relatedInvoiceNumber?: string;
  initiatedBy?: string;
  createdAt: string;
}

interface RawSubscriptionItemResponse {
  id: string;
  subscriptionProductId: string;
  featureName: string;
  featureCode: string;
  unitPrice: number;
  quantity: number;
  locationId?: string;
  locationName?: string;
  createdAt: string;
  // Per-item billing fields (Stripe-Flexible parity). Optional so older
  // backend builds without these fields don't break the FE — fallback path
  // in `rawItemToBillingItem` projects from the subscription-level cycle.
  billingInterval?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
}

interface RawSubscriptionResponse {
  id: string;
  businessId: string;
  planId?: string;
  billingInterval: string;
  status: string;
  currency: string;
  // absent while PARKED: bought at intake, billing starts at go-live (odin billing-starts-at-go-live.md)
  startDate?: string;
  trialEndDate?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  canceledAt?: string;
  lastBilledAt?: string;
  endReason?: string;
  items: RawSubscriptionItemResponse[];
  createdAt: string;
}

interface RawBusinessSubscriptionsResponse {
  businessId: string;
  businessName: string;
  subscription?: RawSubscriptionResponse;
  // Backend ships history alongside the active subscription (newest first).
  // Empty for first-time customers.
  history?: RawSubscriptionResponse[];
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {
  private http = inject(HttpClient);
  private productManagementService = inject(ProductManagementService);

  private readonly monetaV1 = `${environment.baseUrl}/moneta/v1`;

  private invoicesUrl(businessId: string)          { return `${this.monetaV1}/businesses/${businessId}/invoices`; }
  private paymentsUrl(businessId: string)           { return `${this.monetaV1}/businesses/${businessId}/payments`; }
  private paymentProfileUrl(businessId: string)     { return `${this.monetaV1}/businesses/${businessId}/payment-profile`; }
  private paymentInstrumentsUrl(businessId: string) { return `${this.monetaV1}/businesses/${businessId}/payment-instruments`; }
  private creditsUrl(businessId: string)            { return `${this.monetaV1}/businesses/${businessId}/credit-balance`; }
  private subscriptionsUrl(businessId: string)      { return `${this.monetaV1}/businesses/${businessId}/subscriptions`; }
  private locationsUrl(businessId: string)          { return `${this.monetaV1}/businesses/${businessId}/locations`; }

  // odin requires an Idempotency-Key on the payment-profile create (get-or-create).
  // Fresh key per call — the create is safe to repeat, so a new key each time is fine.
  private idempotencyHeaders(): HttpHeaders {
    const key = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new HttpHeaders({ 'Idempotency-Key': key });
  }

  // ---------------------------------------------------------------------------
  // BILLING ACCOUNT — composite read of subscription + invoices + payments +
  // payment-profile + credit balance. Each leg has its own catchError so a
  // single failing endpoint doesn't blow away the whole panel.
  // ---------------------------------------------------------------------------

  /**
   * Walks odin's cursor pages. The endpoint defaults to 20 invoices per page and caps at
   * 100, so a client with a year of monthly billing is silently truncated by one request.
   */
  private listAllInvoices(
    businessId: string,
    pageToken?: string,
    acc: InvoiceSummaryResponse[] = [],
    page = 1
  ): Observable<InvoiceSummaryResponse[]> {
    const MAX_PAGES = 50;   // ~5,000 invoices; a stuck cursor must not loop forever
    const url = pageToken
      ? `${this.invoicesUrl(businessId)}?pageSize=100&pageToken=${encodeURIComponent(pageToken)}`
      : `${this.invoicesUrl(businessId)}?pageSize=100`;

    return this.http.get<PaginatedResult<InvoiceSummaryResponse>>(url).pipe(
      switchMap(res => {
        const all = [...acc, ...(res?.items ?? [])];
        const next = res?.nextKey;
        if (!next || next === pageToken || page >= MAX_PAGES) return of(all);
        return this.listAllInvoices(businessId, next, all, page + 1);
      })
    );
  }

  getBillingAccount(businessId: string): Observable<BillingAccount> {
    return forkJoin({
      subscriptionsResponse: this.http.get<RawBusinessSubscriptionsResponse>(this.subscriptionsUrl(businessId)).pipe(
        catchError(handleError<RawBusinessSubscriptionsResponse | null>('getBillingAccount.subscriptions', null))
      ),
      invoices: this.listAllInvoices(businessId).pipe(
        catchError(handleError<InvoiceSummaryResponse[]>('getBillingAccount.invoices', []))
      ),
      payments: this.http.get<PaginatedResult<PaymentResponse>>(this.paymentsUrl(businessId)).pipe(
        map(res => res?.items ?? []),
        catchError(handleError<PaymentResponse[]>('getBillingAccount.payments', []))
      ),
      paymentProfile: this.http.get<PaymentProfileResponse>(this.paymentProfileUrl(businessId)).pipe(
        catchError(handleError<PaymentProfileResponse | null>('getBillingAccount.paymentProfile', null))
      ),
      creditBalance: this.http.get<CreditBalanceResponse>(this.creditsUrl(businessId)).pipe(
        catchError(handleError<CreditBalanceResponse | null>('getBillingAccount.creditBalance', null))
      )
    }).pipe(
      map(({ subscriptionsResponse, invoices: invoiceSummaries, payments, paymentProfile, creditBalance }) => {
        const invoices: Invoice[] = (invoiceSummaries ?? []).map(s =>
          this.mapInvoiceSummary(s, businessId, payments ?? [])
        );
        const subscription = subscriptionsResponse?.subscription ?? null;
        const history = subscriptionsResponse?.history ?? [];
        const account: BillingAccount = {
          businessId,
          subscription: subscription ? this.mapSubscriptionForBilling(subscription) : null,
          subscriptionHistory: history.map(h => this.mapSubscriptionToHistoryEntry(h)),
          paymentProfile: paymentProfile ? this.mapPaymentProfile(paymentProfile) : null,
          invoices,
          creditBalance: creditBalance
            ? this.mapCreditBalance(creditBalance)
            : { amount: 0, currency: 'USD' },
          // TODO: backend gap — productPurchases has no endpoint yet.
          productPurchases: [],
          // TODO: backend gap — billing statistics has no endpoint yet.
          // Compute lightweight totals from the invoice list so summary tiles
          // show *something* real, not all-zero.
          statistics: this.computeStatisticsFromInvoices(invoices)
        };
        return account;
      })
    );
  }

  /**
   * GET /v1/businesses/{businessId}/locations — paginated on the backend,
   * but most businesses have <10 locations. Request `limit=100` and ignore
   * `nextKey`; loop logic is premature complexity.
   *
   * Slim shape for selectors and dedup. For views that need the full
   * address/phone (e.g. Business Detail General tab), use
   * `getBusinessLocationsDetailed`.
   */
  getBusinessLocations(businessId: string): Observable<SubscriptionItemLocation[]> {
    return this.getBusinessLocationsDetailed(businessId).pipe(
      map(locs => locs.map(l => ({ id: l.id, name: l.name })))
    );
  }

  /** Full `LocationResponse` shape: id, businessId, name, address, phone, email, timezone, createdAt. */
  getBusinessLocationsDetailed(businessId: string): Observable<BusinessLocation[]> {
    const url = `${this.locationsUrl(businessId)}?limit=100`;
    return this.http.get<{ items: BusinessLocation[] }>(url).pipe(
      map(r => r.items ?? []),
      catchError(handleError<BusinessLocation[]>('getBusinessLocationsDetailed', []))
    );
  }

  /**
   * GET /v1/businesses/{businessId}/invoices/{invoiceId}
   * Fetches full invoice detail (line items, totals) and its payments in
   * parallel. Used when the user opens the invoice-detail drawer.
   */
  getInvoiceById(businessId: string, invoiceId: string): Observable<Invoice | null> {
    return forkJoin({
      detail: this.http.get<InvoiceDetailResponse>(`${this.invoicesUrl(businessId)}/${invoiceId}`).pipe(
        catchError(handleError<InvoiceDetailResponse | null>('getInvoiceById.detail', null))
      ),
      payments: this.http.get<PaginatedResult<PaymentResponse>>(`${this.invoicesUrl(businessId)}/${invoiceId}/payments`).pipe(
        map(res => res?.items ?? []),
        catchError(handleError<PaymentResponse[]>('getInvoiceById.payments', []))
      )
    }).pipe(
      map(({ detail, payments }) =>
        detail ? this.mapInvoiceDetail(detail, businessId, payments ?? []) : null
      )
    );
  }

  // ---------------------------------------------------------------------------
  // CROSS-BUSINESS INVOICE QUERY — GET /v1/invoices and /v1/invoices:summary.
  //
  // Not under /businesses/{id}: these are the portfolio-wide reads behind
  // FINANCE › Invoices. Neither pipes through handleError with a fallback —
  // an empty page and "the request failed" are different answers, and the
  // screen renders an error card with Retry for the second.
  // ---------------------------------------------------------------------------

  /**
   * One page of invoices across every client, `due_date ASC NULLS LAST`. Filters are the
   * contract's, verbatim; anything left out of `filters` is not sent, so the server applies
   * its own default (every status, every amount). The unpaid slice is the caller's to ask for.
   */
  listInvoices(filters: InvoiceListFilters, pageToken?: string | null, pageSize = 25): Observable<InvoicePage> {
    let params = new HttpParams();
    const set = (key: string, value: string | number | null | undefined): void => {
      if (value === null || value === undefined || value === '') return;
      params = params.set(key, String(value));
    };
    // multi-value status is comma-separated on the wire, not repeated keys
    if (filters.status?.length) set('status', filters.status.join(','));
    set('minAmountDue', filters.minAmountDue);
    set('maxAmountDue', filters.maxAmountDue);
    set('businessId', filters.businessId);
    set('currency', filters.currency);
    set('businessName', filters.businessName);
    set('agedDays', filters.agedDays);
    set('dueDateFrom', filters.dueDateFrom);
    set('dueDateTo', filters.dueDateTo);
    set('intentState', filters.intentState);
    set('pageToken', pageToken);
    set('pageSize', pageSize);

    return this.http.get<InvoicePage>(`${this.monetaV1}/invoices`, { params }).pipe(
      map(res => ({
        items: res?.items ?? [],
        total: res?.total ?? null,
        nextKey: res?.nextKey ?? null
      })),
      catchError(handleError('listInvoices'))
    );
  }

  /**
   * Counts and dollars at risk, grouped by status, by age band and by collection state.
   * Omit `currency` to get every currency back, one bucket row per currency.
   */
  invoiceSummary(filters: InvoiceSummaryFilters = {}): Observable<InvoiceSummary> {
    let params = new HttpParams();
    if (filters.status?.length) params = params.set('status', filters.status.join(','));
    if (filters.currency) params = params.set('currency', filters.currency);

    return this.http.get<InvoiceSummary>(`${this.monetaV1}/invoices:summary`, { params }).pipe(
      map(res => ({
        asOf: res?.asOf,
        totalCount: res?.totalCount ?? 0,
        totalAmountDue: res?.totalAmountDue ?? 0,
        currency: res?.currency ?? null,
        byStatus: res?.byStatus ?? [],
        byAge: res?.byAge ?? [],
        byIntentState: res?.byIntentState ?? []
      } as InvoiceSummary)),
      catchError(handleError('invoiceSummary'))
    );
  }

  // ---------------------------------------------------------------------------
  // CATALOG — derived from ProductManagementService (unchanged from Phase 1).
  // ---------------------------------------------------------------------------

  /**
   * Catalog driven by SubscriptionProduct rows. Each entry carries both the
   * monthly and yearly SP ids (when they exist) so the Add-Item flow can
   * forward the correct SP UUID to `POST /items` matching the subscription's
   * current billing interval. Backend enforces uniqueness `(feature, interval)`
   * so the (monthly|yearly) slot is unambiguous.
   */
  getAvailableProductsWithPricing(): Observable<AvailableProduct[]> {
    return this.productManagementService.getSubscriptionProducts().pipe(
      map(subscriptionProducts => {
        const featureMap = new Map<string, { monthly?: SubscriptionProduct; yearly?: SubscriptionProduct }>();
        subscriptionProducts.forEach(sp => {
          if (!featureMap.has(sp.featureId)) featureMap.set(sp.featureId, {});
          const entry = featureMap.get(sp.featureId)!;
          if (sp.billingInterval === 'MONTHLY') entry.monthly = sp;
          else if (sp.billingInterval === 'YEARLY') entry.yearly = sp;
        });
        const products: AvailableProduct[] = [];
        featureMap.forEach((pricing, featureId) => {
          const ref = pricing.monthly ?? pricing.yearly;
          if (!ref) return;
          const featureCode = (ref.featureCode ?? '').toLowerCase();
          const monthlyCents = pricing.monthly?.price;
          const yearlyCents = pricing.yearly?.price;
          products.push({
            id: featureId,
            featureCode,
            name: ref.featureName,
            description: '',
            monthlyPrice: monthlyCents != null ? monthlyCents / 100 : 0,
            yearlyPrice: yearlyCents != null ? yearlyCents / 100 : 0,
            monthlyPriceCents: monthlyCents,
            yearlyPriceCents: yearlyCents,
            monthlySubscriptionProductId: pricing.monthly?.id,
            yearlySubscriptionProductId: pricing.yearly?.id,
            icon: FEATURE_ICONS[featureCode] || 'category',
            isActive: ref.isActive
          });
        });
        return products;
      })
    );
  }

  /** @deprecated kept for callers that haven't migrated; prefer `getAvailableProductsWithPricing` */
  getAvailableProducts(): Observable<AvailableProduct[]> {
    return this.getAvailableProductsWithPricing();
  }

  // Proration preview: moneta has no endpoint, so Juno has none either. The stub that used
  // to live here invented a credit, a charge and a total, and nothing called it. A screen that
  // needs this number shows "not built yet" until moneta can compute it.

  // ---------------------------------------------------------------------------
  // CREDIT BALANCE — POST /v1/businesses/{businessId}/credit-balance:adjust
  // ---------------------------------------------------------------------------

  /**
   * Adjust credit balance. `amount` is in dollars (positive to credit, negative
   * to debit). Returns the new balance derived from `transaction.balanceAfter`.
   */
  adjustCredit(businessId: string, amount: number, reason: string): Observable<CreditBalance> {
    const body = { amount: Math.round(amount * 100), reason };
    // NOT piped through handleError. odin rejects an adjustment that would drive the
    // balance negative (400), and the old fallback returned `{amount: 0}` — which the UI
    // would render as "balance is now zero" after a rejected debit. Let it throw.
    return this.http.post<CreditBalanceTransactionResponse>(`${this.creditsUrl(businessId)}:adjust`, body, { headers: this.idempotencyHeaders() }).pipe(
      map(res => ({ amount: res.balanceAfter / 100, currency: 'USD' } as CreditBalance))
    );
  }

  /**
   * Ledger behind the balance — GET /v1/businesses/{businessId}/credit-transactions.
   * TOP_UP / DRAWDOWN / ADJUSTMENT / EXPIRY, newest first.
   */
  getCreditTransactions(businessId: string, pageSize = 50): Observable<CreditTransaction[]> {
    const params = new HttpParams().set('pageSize', String(pageSize));
    return this.http.get<PaginatedResult<CreditBalanceTransactionResponse>>(
      `${this.monetaV1}/businesses/${businessId}/credit-transactions`, { params }
    ).pipe(
      map(res => (res?.items ?? []).map(t => ({
        id: t.id,
        type: t.type,
        amount: (t.amount ?? 0) / 100,
        balanceAfter: (t.balanceAfter ?? 0) / 100,
        description: t.description,
        relatedInvoiceNumber: t.relatedInvoiceNumber,
        initiatedBy: t.initiatedBy,
        createdAt: t.createdAt,
      } as CreditTransaction))),
      catchError(handleError<CreditTransaction[]>('getCreditTransactions', []))
    );
  }

  // ---------------------------------------------------------------------------
  // PAYMENT PROFILE & PAYMENT INSTRUMENTS
  //
  // Surfaces the full 7-endpoint contract from odin moneta PaymentInstrumentApi:
  //   GET    /payment-profile                       getPaymentProfile
  //   POST   /payment-profile                       getOrCreatePaymentProfile
  //   PUT    /payment-profile                       updatePaymentProfile
  //   GET    /payment-instruments                   listPaymentInstruments
  //   POST   /payment-instruments                   addPaymentInstrument
  //   POST   /payment-instruments:set-default       setDefaultPaymentMethod
  //   DELETE /payment-instruments/{id}              deletePaymentMethod
  // ---------------------------------------------------------------------------

  /** GET /v1/businesses/{businessId}/payment-profile — 404 if profile not yet created: a normal answer, logged quietly. */
  getPaymentProfile(businessId: string): Observable<PaymentProfile | null> {
    return this.http.get<PaymentProfileResponse>(this.paymentProfileUrl(businessId)).pipe(
      map(res => res ? this.mapPaymentProfile(res) : null),
      catchError(handleError<PaymentProfile | null>('getPaymentProfile', null, {quiet: [404]}))
    );
  }

  /**
   * GET /v1/businesses/{businessId}/payment-profile for a screen that must tell "none yet" from "could
   * not ask": null only on moneta's 404, any other failure rethrows. RAW_HTTP_ERROR keeps the status:
   * without it the error interceptor hands back a sentence, and no 404 could be told apart.
   */
  findPaymentProfile(businessId: string): Observable<PaymentProfile | null> {
    return this.http.get<PaymentProfileResponse>(this.paymentProfileUrl(businessId), {
      context: new HttpContext().set(RAW_HTTP_ERROR, true),
    }).pipe(
      map(res => res ? this.mapPaymentProfile(res) : null),
      catchError(err => err?.status === 404 ? of(null) : throwError(() => err))
    );
  }

  /**
   * POST /v1/businesses/{businessId}/payment-profile — idempotent.
   * Returns the existing profile if one exists, otherwise creates an empty one.
   * Use this when entering the Payment Profile tab so admins can manage a
   * profile even before any subscription or first instrument exists.
   */
  getOrCreatePaymentProfile(businessId: string): Observable<PaymentProfile | null> {
    return this.http.post<PaymentProfileResponse>(this.paymentProfileUrl(businessId), {}, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapPaymentProfile(res) : null),
      catchError(handleError<PaymentProfile | null>('getOrCreatePaymentProfile', null))
    );
  }

  /**
   * PUT /v1/businesses/{businessId}/payment-profile — patch billing email
   * and/or auto-pay. Pass only the fields that are changing.
   */
  updatePaymentProfile(businessId: string, req: UpdatePaymentProfileRequest): Observable<PaymentProfile | null> {
    return this.http.put<PaymentProfileResponse>(this.paymentProfileUrl(businessId), req, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapPaymentProfile(res) : null),
      catchError(handleError<PaymentProfile | null>('updatePaymentProfile', null))
    );
  }

  /** GET /v1/businesses/{businessId}/payment-instruments — excludes REMOVED rows. A page, like every moneta list. */
  listPaymentInstruments(businessId: string): Observable<PaymentInstrument[]> {
    return this.http.get<PaginatedResult<PaymentInstrumentResponse>>(this.paymentInstrumentsUrl(businessId)).pipe(
      map(res => (res?.items ?? []).map(i => this.mapInstrument(i, null))),
      catchError(handleError<PaymentInstrument[]>('listPaymentInstruments', []))
    );
  }

  /**
   * POST /v1/businesses/{businessId}/payment-instruments.
   * setAsDefault=true is honored automatically when this is the first
   * instrument on the profile.
   *
   * NOT piped through handleError: moneta verifies the card first, so this can fail with
   * 422 (declined, reason in the body) or 502, saving nothing. Callers MUST supply an
   * error handler and show the reason — see errorMessage() in handle-error.ts.
   */
  addPaymentInstrument(businessId: string, req: CreatePaymentInstrumentRequest): Observable<PaymentInstrument | null> {
    return this.http.post<PaymentInstrumentResponse>(this.paymentInstrumentsUrl(businessId), req, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapInstrument(res, null) : null)
    );
  }

  /** POST /v1/businesses/{businessId}/payment-instruments:set-default (`PaymentInstrumentApi.kt:96`). */
  setDefaultPaymentMethod(businessId: string, paymentMethodId: string): Observable<boolean> {
    const body = { instrumentId: paymentMethodId };
    return this.http.post<PaymentProfileResponse>(`${this.paymentInstrumentsUrl(businessId)}:set-default`, body, { headers: this.idempotencyHeaders() }).pipe(
      map(() => true),
      catchError(handleError<boolean>('setDefaultPaymentMethod', false))
    );
  }

  /**
   * DELETE /v1/businesses/{businessId}/payment-instruments/{id} — soft delete.
   * Backend integrity guarantee: if this was the default, it will be cleared
   * and another ACTIVE instrument promoted automatically.
   */
  deletePaymentMethod(businessId: string, paymentMethodId: string): Observable<boolean> {
    return this.http.delete<void>(`${this.paymentInstrumentsUrl(businessId)}/${paymentMethodId}`, { headers: this.idempotencyHeaders() }).pipe(
      map(() => true),
      catchError(handleError<boolean>('deletePaymentMethod', false))
    );
  }

  // ---------------------------------------------------------------------------
  // SUBSCRIPTION — modify is non-atomic on backend; left as backend gap.
  // ---------------------------------------------------------------------------

  //  TODO: backend gap — no atomic "swap product set" endpoint. Composing
  // multiple add/remove/update calls would need optimistic rollback the
  // backend doesn't support yet. Returns null so the caller can no-op.
  modifySubscription(_businessId: string, _productIds: string[], _billingCycle: 'MONTHLY' | 'YEARLY'): Observable<Subscription | null> {
    return of(null);
  }

  /**
   * Phase 2: real backend cancel via ProductManagementService.
   * Two-step: fetch subscription to learn its id, then DELETE with reason.
   * Returns false if no subscription exists or the cancel call failed.
   *
   * Default reason is CANCELLED_BY_ADMIN — this is the admin tool, so a
   * customer-initiated cancel never originates here.
   */
  cancelSubscription(
    businessId: string,
    endReason: SubscriptionEndReason = 'CANCELLED_BY_ADMIN'
  ): Observable<boolean> {
    return this.productManagementService.getSubscriptionByBusinessId(businessId).pipe(
      switchMap(sub => {
        if (!sub) return of(false);
        return this.productManagementService.cancelSubscription(businessId, sub.id, endReason).pipe(
          map(result => result !== null)
        );
      })
    );
  }

  /**
   * Add one or more subscription items.
   *
   * `locationIds === null` ⇒ a single business-wide item.
   * `locationIds` array ⇒ fan out one POST per location (backend rejects
   * arrays — one row per (subscription, SP, location) tuple is the model).
   *
   * `unitPriceOverrideCents` is forwarded verbatim; pass `null` to use the
   * catalog price. Fails the whole call if any leg returns null (no fake
   * fallback — silent fakes were the source of the "looks like it worked"
   * bug).
   */
  addSubscriptionItem(
    businessId: string,
    subscriptionProductId: string,
    locationIds: string[] | null,
    unitPriceOverrideCents: number | null
  ): Observable<SubscriptionItem[]> {
    return this.productManagementService.getSubscriptionByBusinessId(businessId).pipe(
      switchMap(sub => {
        if (!sub) {
          return throwError(() => new Error('No subscription on this business — create one first.'));
        }
        const existingIds = new Set((sub.items ?? []).map(i => i.id));
        const targets: (string | null)[] = locationIds && locationIds.length > 0 ? locationIds : [null];
        const calls = targets.map(locationId =>
          this.productManagementService.addSubscriptionItem(businessId, sub.id, {
            subscriptionProductId,
            quantity: 1,
            locationId: locationId ?? undefined,
            unitPriceOverride: unitPriceOverrideCents ?? undefined
          })
        );
        return forkJoin(calls).pipe(
          map(results => {
            const failed = results.some(r => r == null);
            if (failed) throw new Error('Failed to add subscription item.');
            // The last response carries the full, post-add subscription. Diff
            // against the items present before the call to surface only what
            // was actually created.
            const last = results[results.length - 1]!;
            return (last.items ?? [])
              .filter(it => !existingIds.has(it.id))
              .map(it => this.pmItemToBillingItem(it, it.locationId ?? null));
          })
        );
      })
    );
  }

  // ---------------------------------------------------------------------------
  // INVOICE WRITE — :void / :credit-note
  // ---------------------------------------------------------------------------

  /**
   * DEMO/DEV — force-runs billing for this business now (odin `:trigger-billing`).
   * Closes the current ACTIVE period regardless of its end date, issues the invoice
   * for the full period, runs collection (may charge the default card), and opens
   * the next period. Errors surface to the caller — a failed run must reach the AM.
   */
  triggerBilling(businessId: string): Observable<TriggerBillingResult> {
    // odin idempotent_config/moneta.json: Idempotency-Key required — a retried run must not bill twice.
    return this.http.post<TriggerBillingResult>(
      `${this.monetaV1}/businesses/${businessId}:trigger-billing`, {}, { headers: this.idempotencyHeaders() }
    );
  }

  /**
   * Charge the invoice on the business's default card, synchronously. CUSTOMER_INITIATED
   * on the odin side: bypasses the auto-pay flag and the retry timing gate — a human
   * clicked, so it always tries. Errors surface to the caller (no handleError): the AM
   * must see the gateway's reason, not an empty result.
   */
  payInvoice(businessId: string, invoiceId: string, paymentInstrumentId?: string): Observable<PayInvoiceResult> {
    return this.http.post<PayInvoiceResult>(
      `${this.invoicesUrl(businessId)}/${invoiceId}:pay`,
      paymentInstrumentId ? { paymentInstrumentId } : {},
      { headers: this.idempotencyHeaders() }
    );
  }

  /**
   * Record a payment that happened outside the gateway (wire, check, cash). `amount` is
   * in cents. Errors surface to the caller — a silently dropped record is a books error.
   */
  recordExternalPayment(
    businessId: string,
    invoiceId: string,
    amountCents: number,
    referenceNumber?: string,
    notes?: string
  ): Observable<PaymentResponse> {
    return this.http.post<PaymentResponse>(
      `${this.paymentsUrl(businessId)}:record-external`,
      { invoiceId, amount: amountCents, referenceNumber: referenceNumber || null, notes: notes || null },
      { headers: this.idempotencyHeaders() }
    );
  }

  voidInvoice(businessId: string, invoiceId: string): Observable<boolean> {
    return this.http.post<InvoiceDetailResponse>(`${this.invoicesUrl(businessId)}/${invoiceId}:void`, {}, { headers: this.idempotencyHeaders() }).pipe(
      map(() => true),
      catchError(handleError<boolean>('voidInvoice', false))
    );
  }

  /**
   * Write the invoice off as bad debt — the AM gave up collecting. moneta cancels the
   * collection intent in the same transaction, and the balance stays on the books.
   * It refuses anything but an ACTIVE invoice with a balance due (`Invoice.writeOff`),
   * so the reason must reach the AM: no handleError fallback here.
   */
  markUncollectible(businessId: string, invoiceId: string): Observable<Invoice> {
    return this.http.post<InvoiceDetailResponse>(
      `${this.invoicesUrl(businessId)}/${invoiceId}:mark-uncollectible`, {}, { headers: this.idempotencyHeaders() }
    ).pipe(
      map(res => this.mapInvoiceDetail(res, businessId, []))
    );
  }

  /**
   * Credit note against an invoice money already moved on. `amount` is in dollars; the
   * line item carries the reason so the credit reads for itself on the client's statement.
   *
   * moneta rejects a credit note where nothing was paid (void the original instead) and
   * one against another credit note — both 400 with the reason in the body, so this call
   * rethrows and the caller shows it.
   */
  createCreditNote(
    businessId: string,
    invoiceId: string,
    invoiceNumber: string,
    amount: number,
    reason: string
  ): Observable<Invoice> {
    const body = {
      referenceInvoiceId: invoiceId,
      reason,
      lineItems: [{
        description: `Credit for invoice ${invoiceNumber}: ${reason}`,
        quantity: 1,
        unitPrice: Math.round(amount * 100)
      }]
    };
    // Colon action is kebab-case server-side, and hangs off the collection, not one invoice
    // (`InvoiceApi.kt`): the credit note is a new invoice that references the original.
    return this.http.post<InvoiceDetailResponse>(
      `${this.invoicesUrl(businessId)}:credit-note`, body, { headers: this.idempotencyHeaders() }
    ).pipe(
      map(res => this.mapInvoiceDetail(res, businessId, []))
    );
  }

  // Invoice PDF: moneta renders none, so this used to hand back a blob containing the words
  // "PDF content" — a file the AM could have sent to a client. Nothing called it. When moneta
  // can render one, this is where it goes.

  // ===========================================================================
  // PRIVATE — DTO mappers (backend → frontend Billing model)
  // ===========================================================================

  private mapInvoiceSummary(s: InvoiceSummaryResponse, businessId: string, allPayments: PaymentResponse[]): Invoice {
    const invoicePayments = allPayments.filter(p => p.invoiceId === s.id);
    const amountPaidCents = invoicePayments.reduce((sum, p) => sum + p.amount, 0);
    const totalDollars = s.total / 100;
    const amountPaidDollars = amountPaidCents / 100;
    return {
      id: s.id,
      invoiceNumber: s.invoiceNumber,
      type: s.type as InvoiceType,
      status: s.status as InvoiceStatus,
      businessId,
      currency: 'USD',
      periodStart: new Date(s.periodStart),
      periodEnd: new Date(s.periodEnd),
      // Summary endpoint doesn't return subtotal/tax/discount — surface `total`
      // for both subtotal and total so the row renders the right amount.
      subtotal: totalDollars,
      taxAmount: 0,
      discountAmount: 0,
      total: totalDollars,
      amountPaid: amountPaidDollars,
      amountDue: s.status === 'PAID' ? 0 : Math.max(totalDollars - amountPaidDollars, 0),
      // createdAt isn't in summary; fall back to periodStart so the date column
      // isn't empty. Detail endpoint has the real createdAt.
      createdAt: new Date(s.periodStart),
      lineItems: [],
      payments: invoicePayments.map(p => this.mapPayment(p))
    };
  }

  private mapInvoiceDetail(d: InvoiceDetailResponse, businessId: string, payments: PaymentResponse[]): Invoice {
    return {
      id: d.id,
      invoiceNumber: d.invoiceNumber,
      type: d.type as InvoiceType,
      status: d.status as InvoiceStatus,
      businessId,
      currency: d.currency,
      periodStart: new Date(d.periodStart),
      periodEnd: new Date(d.periodEnd),
      subtotal: d.subtotal / 100,
      taxAmount: d.taxAmount / 100,
      discountAmount: d.discountAmount / 100,
      total: d.total / 100,
      amountPaid: d.amountPaid / 100,
      amountDue: d.amountDue / 100,
      dueDate: d.dueDate ? new Date(d.dueDate) : undefined,
      paidAt: d.paidAt ? new Date(d.paidAt) : undefined,
      createdAt: new Date(d.createdAt),
      lineItems: (d.lineItems ?? []).map(li => this.mapInvoiceLineItem(li)),
      payments: payments.map(p => this.mapPayment(p)),
      collection: d.collection ? this.mapInvoiceCollection(d.collection) : undefined
    };
  }

  private mapInvoiceCollection(c: InvoiceCollectionResponse): InvoiceCollection {
    return {
      status: c.status,
      totalAttempts: c.totalAttempts,
      maxAttempts: c.maxAttempts,
      nextRetryAt: c.nextRetryAt ? new Date(c.nextRetryAt) : undefined,
      lastFailureReason: c.lastFailureReason,
      lastFailureMessage: c.lastFailureMessage,
      blockedBy: c.blockedBy
    };
  }

  private mapInvoiceLineItem(li: InvoiceLineItemResponse): InvoiceLineItem {
    return {
      id: li.id,
      description: li.description,
      quantity: li.quantity,
      unitPrice: li.unitPrice / 100,
      amount: li.amount / 100
    };
  }

  private mapPayment(p: PaymentResponse): Payment {
    return {
      id: p.id,
      amount: p.amount / 100,
      method: this.mapPaymentMethod(p.method),
      // Backend `PaymentResponse` doesn't expose status; assume completed since
      // it shows up on /payments at all (failed gateway charges don't get rows).
      status: 'COMPLETED',
      processedAt: p.processedAt ? new Date(p.processedAt) : new Date(p.createdAt)
    };
  }

  private mapPaymentProfile(p: PaymentProfileResponse): PaymentProfile {
    const defaultId = p.defaultInstrumentId ?? null;
    return {
      id: p.id,
      businessId: p.businessId,
      billingEmail: p.billingEmail ?? null,
      autoPayEnabled: p.autoPayEnabled,
      defaultInstrumentId: defaultId,
      gatewayCustomerId: p.gatewayCustomerId ?? null,
      createdAt: p.createdAt,
      instruments: (p.instruments ?? []).map(i => this.mapInstrument(i, defaultId))
    };
  }

  private mapInstrument(i: PaymentInstrumentResponse, defaultId: string | null): PaymentInstrument {
    return {
      id: i.id,
      type: i.type as InstrumentType,
      status: i.status as InstrumentStatus,
      gatewayName: i.gatewayName,
      displayName: i.displayName,
      lastFour: i.lastFour ?? null,
      brand: i.brand ?? null,
      expiresAt: i.expiresAt ?? null,
      bankName: i.bankName ?? null,
      // Service guarantees `isDefault` matches profile.defaultInstrumentId,
      // but we recompute defensively because `listPaymentInstruments` can
      // return a row with isDefault=false if called without the profile leg.
      isDefault: i.isDefault || (!!defaultId && i.id === defaultId),
      createdAt: i.createdAt
    };
  }

  private mapCreditBalance(c: CreditBalanceResponse): CreditBalance {
    return { amount: c.balanceAmount / 100, currency: c.currency };
  }

  private mapSubscriptionForBilling(s: RawSubscriptionResponse): Subscription {
    // A parked subscription (bought at intake, client not LIVE) has neither a period nor a start:
    // keep null so the UI shows "—", never an Invalid Date.
    const periodStart = s.currentPeriodStart
      ? new Date(s.currentPeriodStart)
      : (s.startDate ? new Date(s.startDate) : null);
    // No fake fallback: if the backend has no period end, keep it null so the UI
    // shows "—" instead of today's date pretending to be the next payment.
    const periodEnd = s.currentPeriodEnd ? new Date(s.currentPeriodEnd) : null;
    return {
      id: s.id,
      status: this.mapSubscriptionStatus(s.status),
      billingCycle: (s.billingInterval as BillingCycle) ?? 'MONTHLY',
      currentPeriodStart: periodStart,
      currentPeriodEnd: periodEnd,
      totalMonthlyAmount: this.computeMonthlyAmount(s.items ?? [], s.billingInterval),
      items: (s.items ?? []).map(it => this.rawItemToBillingItem(it, s.billingInterval)),
      // TODO: backend gap — UsageApi exists but mapping to MeteredProduct
      // (used/limit/unit) needs follow-up DTO work. Empty for now.
      meteredProducts: []
    };
  }

  private rawItemToBillingItem(it: RawSubscriptionItemResponse, subscriptionInterval: string): SubscriptionItem {
    const code = (it.featureCode ?? '').toLowerCase();
    // Per-item interval (Stripe-Flexible). Falls back to subscription-level
    // interval when the backend response is from an older build that only
    // populates the parent field.
    const itemInterval = (it.billingInterval ?? subscriptionInterval ?? 'MONTHLY') as BillingCycle;
    return {
      id: it.id,
      subscriptionProduct: {
        id: it.subscriptionProductId,
        featureId: '',
        featureCode: it.featureCode,
        featureName: it.featureName,
        // featureCategory not on subscription-item DTO; default to ADDON.
        // UI uses this only for the badge color.
        featureCategory: 'ADDON',
        billingInterval: itemInterval,
        price: it.unitPrice / 100,
        icon: FEATURE_ICONS[code] || 'category'
      },
      unitPrice: it.unitPrice / 100,
      quantity: it.quantity,
      location: it.locationId ? { id: it.locationId, name: it.locationName ?? 'Location' } : null,
      createdAt: new Date(it.createdAt),
      currentPeriodEnd: it.currentPeriodEnd ? new Date(it.currentPeriodEnd) : undefined
    };
  }

  private mapSubscriptionToHistoryEntry(s: RawSubscriptionResponse): SubscriptionHistoryEntry {
    const startedAt = new Date(s.startDate ?? s.createdAt);
    const endedAt = s.canceledAt
      ? new Date(s.canceledAt)
      : (s.currentPeriodEnd ? new Date(s.currentPeriodEnd) : startedAt);
    const totalCents = (s.items ?? []).reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);
    const billingCycle = (s.billingInterval as BillingCycle) ?? 'MONTHLY';
    return {
      id: s.id,
      planName: billingCycle === 'YEARLY' ? 'Yearly Plan' : 'Monthly Plan',
      status: this.mapSubscriptionStatus(s.status),
      startedAt,
      endedAt,
      endReason: this.mapEndReason(s.endReason),
      totalAmount: totalCents / 100,
      billingCycle
    };
  }

  private mapEndReason(raw?: string): SubscriptionEndReason {
    switch (raw) {
      case 'CANCELLED_BY_CUSTOMER':
      case 'CANCELLED_BY_ADMIN':
      case 'NON_PAYMENT':
      case 'PLAN_CHANGE':
      case 'EXPIRED':
        return raw;
      default:
        return 'CANCELLED_BY_ADMIN';
    }
  }

  private computeMonthlyAmount(items: RawSubscriptionItemResponse[], billingInterval: string): number {
    const totalCents = items.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);
    const dollars = totalCents / 100;
    return billingInterval === 'YEARLY' ? dollars / 12 : dollars;
  }

  private mapSubscriptionStatus(s: string): SubscriptionStatus {
    switch (s) {
      case 'CANCELED':
      case 'CANCELLED':
        return 'CANCELLED';
      case 'EXPIRED':
        return 'EXPIRED';
      case 'TRIAL':
      case 'TRIALING':
        return 'TRIALING';
      case 'PAST_DUE':
        return 'PAST_DUE';
      case 'PAUSED':
        // Frontend `SubscriptionStatus` doesn't model PAUSED — surface as
        // CANCELLED so the UI shows the inactive treatment.
        return 'CANCELLED';
      default:
        return 'ACTIVE';
    }
  }

  private mapPaymentMethod(m: string): PaymentMethod {
    if (m === 'CARD' || m === 'GATEWAY') return 'CARD';
    if (m === 'CREDIT_BALANCE') return 'CREDIT_BALANCE';
    // BANK_TRANSFER, CASH, CHECK, EXTERNAL → BANK_TRANSFER bucket.
    return 'BANK_TRANSFER';
  }

  private computeStatisticsFromInvoices(invoices: Invoice[]): BillingStatistics {
    const paid = invoices.filter(i => i.status === 'PAID');
    const open = invoices.filter(i => i.status === 'OPEN');
    const totalRevenue = paid.reduce((s, i) => s + (i.total > 0 ? i.total : 0), 0);
    const outstandingBalance = open.reduce((s, i) => s + i.amountDue, 0);
    return {
      totalRevenue,
      revenueThisMonth: 0,
      revenueLastMonth: 0,
      revenueGrowth: 0,
      outstandingBalance,
      overdueAmount: 0,
      totalInvoices: invoices.length,
      paidInvoices: paid.length,
      openInvoices: open.length,
      averagePaymentDays: 0,
      revenueBreakdown: { subscriptionRevenue: 0, meteredRevenue: 0, productRevenue: 0 }
    };
  }

  // ---------------------------------------------------------------------------
  // PRIVATE — Phase 2 helpers (PM SubscriptionItem → Billing SubscriptionItem)
  // ---------------------------------------------------------------------------

  private pmItemToBillingItem(item: PmSubscriptionItem, locationId: string | null): SubscriptionItem {
    return {
      id: item.id,
      subscriptionProduct: {
        id: item.subscriptionProductId,
        featureId: '',
        featureCode: item.featureCode,
        featureName: item.featureName,
        featureCategory: 'ADDON',
        billingInterval: 'MONTHLY',
        price: item.unitPrice / 100,
        icon: FEATURE_ICONS[(item.featureCode ?? '').toLowerCase()] || 'add_circle'
      },
      unitPrice: item.unitPrice / 100,
      quantity: item.quantity,
      location: locationId ? { id: locationId, name: 'Location' } : null,
      createdAt: new Date(item.createdAt)
    };
  }

}
